import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';
import './Landing.css';
import LegalModal from './LegalModal';
import {
  HeartIcon,
  BrainIcon,
  ChartIcon,
  ZapIcon
} from './Icons';
import {
  API,
  getAuth,
  persistAuth,
  clearAuth,
  fetchProfileAndCompletionStatus,
} from '../api';

function LandingPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [legalModalType, setLegalModalType] = useState(null);

  const navigate = useNavigate();

  // On mount, check whether an already-authenticated user just landed here
  // (e.g. via back button, direct URL, or because they never logged out).
  // - Authed + profileComplete → /dashboard
  // - Authed + profile incomplete → /register (resumes Step 2+)
  // - Not authed → show landing as normal
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const existing = getAuth();
      if (!existing) {
        if (!cancelled) setBootstrapping(false);
        return;
      }
      const result = await fetchProfileAndCompletionStatus();
      if (cancelled) return;
      if (!result) {
        // Stored token was rejected — clear and let them re-login.
        clearAuth();
        setBootstrapping(false);
        return;
      }
      if (result.profileComplete) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/register', { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const handleLogin = async () => {
    setError(null);
    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(API.login(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const msg = await response.text();
        throw new Error(msg || 'Login failed');
      }

      // Backend returns JSON: { accessToken, refreshToken, userId }.
      // (The old code parsed response.text() and dropped refreshToken/userId.)
      const data = await response.json();
      if (!data.accessToken || !data.userId) {
        throw new Error('Sign-in response was incomplete. Please try again.');
      }

      persistAuth({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        userId: data.userId,
      });

      // Route based on whether this account has finished onboarding.
      // Without this check, a user whose profile is incomplete would land
      // on /dashboard and then get bounced to /register by ProtectedRoute —
      // works but adds an ugly flicker.
      const result = await fetchProfileAndCompletionStatus();
      if (result?.profileComplete) {
        navigate('/dashboard');
      } else {
        navigate('/register');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (bootstrapping) {
    return (
      <div style={bootstrapStyles.root}>
        <div style={bootstrapStyles.spinner} />
      </div>
    );
  }

  return (
    <div className="landing-page-wrapper">
      {/* Logo Section */}
      <div className="landing-logo-wrap">
        <img
          src={require('../assets/onboarding/938a8ebc-324d-4470-88d9-3ac7c40c90d6.png')}
          alt="HRV-4 Logo"
          className="landing-logo-img"
        />
      </div>

      <p className="landing-tagline">Best companion for your heart</p>

      <div className="landing-content">

        {/* Main Hero & Features Column */}
        <div className="landing-left">

          {/* Hero Section */}
          <section className="hero-section">
            <h1 className="hero-title">
              Master Your Health with HRV-4
            </h1>
            <p className="hero-subtitle">
              Unlock the secrets of your body. We track, analyze, and interpret your Heart Rate Variability using cutting-edge Machine Learning to optimize your recovery and performance.
            </p>
            
            {/* Icon Bullets */}
            <div className="landing-icon-bullets">
              <div className="landing-icon-bullet-row">
                <img
                  src={require('../assets/onboarding/fi-ss-chart-histogram.png')}
                  alt=""
                  className="landing-icon-bullet-img"
                />
                <span className="landing-icon-bullet-text">Track your progress over time</span>
              </div>
              <div className="landing-icon-bullet-row">
                <img
                  src={require('../assets/onboarding/heart-rate-pulse-graph.png')}
                  alt=""
                  className="landing-icon-bullet-img"
                />
                <span className="landing-icon-bullet-text">Get alerts on your HRV</span>
              </div>
              <div className="landing-icon-bullet-row">
                <img
                  src={require('../assets/onboarding/Vector.png')}
                  alt=""
                  className="landing-icon-bullet-img"
                />
                <span className="landing-icon-bullet-text">Improve your sleep quality</span>
              </div>
              <div className="landing-icon-bullet-row">
                <img
                  src={require('../assets/onboarding/Watch.png')}
                  alt=""
                  className="landing-icon-bullet-img"
                />
                <span className="landing-icon-bullet-text">Connect your devices easily</span>
              </div>
            </div>
          </section>

          {/* Features Grid */}
          <section className="features-grid" id="features">
            <div className="feature-card">
              <div className="feature-icon">
                <HeartIcon size={24} color="#5CB89A" />
              </div>
              <h3>What is HRV?</h3>
              <p>Heart Rate Variability is the #1 biomarker for stress and recovery. We measure the time gaps between your heartbeats to decode your nervous system.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <BrainIcon size={24} color="#5CB89A" />
              </div>
              <h3>ML-Powered Analysis</h3>
              <p>Our advanced Machine Learning algorithms detect patterns hidden in the noise, predicting fatigue before you even feel it.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <ChartIcon size={24} color="#5CB89A" />
              </div>
              <h3>Actionable Trends</h3>
              <p>Don't just see data—understand it. Get daily scores and feedback that tell you exactly how hard to push.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <ZapIcon size={24} color="#5CB89A" />
              </div>
              <h3>Optimize Performance</h3>
              <p>Align your workouts, sleep, and lifestyle with your body's true capacity.</p>
            </div>
          </section>
        </div>

        {/* Right Column: Login/Register Placeholder */}
        <div className="landing-right">
          <div className="auth-card" id="login-form">
            <div className="auth-header">
              <h2>Get Started</h2>
              <p>Join HRV-4 today</p>
            </div>

            {/* Placeholder Area for Auth Forms */}
            <div className="auth-placeholder-area">
              <div className="input-group">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="name@example.com"
                  className="mock-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="input-group">
                <label>Password</label>
                <input
                  type="password"
                  placeholder="••••••••"
                  className="mock-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <div className="pill-btn-wrapper">
                <button className="pill-btn-primary" onClick={handleLogin} disabled={loading}>
                  <span className="pill-btn-arrow">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </span>
                  <span className="pill-btn-text">{loading ? 'Signing in...' : 'Log In'}</span>
                </button>
              </div>

              {error && <p className="auth-error">{error}</p>}

              <div className="auth-divider">or</div>

              <div className="pill-btn-wrapper">
                <button 
                  className="pill-btn-secondary" 
                  onClick={() => navigate('/register')}
                >
                  <span className="pill-btn-text">Create New Account</span>
                </button>
              </div>
            </div>

            <p className="auth-footer-text">
              By continuing, you agree to our{' '}
              <button className="footer-link" onClick={() => setLegalModalType('terms')}>
                Terms of Service
              </button>{' '}
              and{' '}
              <button className="footer-link" onClick={() => setLegalModalType('privacy')}>
                Privacy Policy
              </button>.
            </p>
          </div>
        </div>

      </div>

      <Footer />

      {legalModalType && (
        <LegalModal type={legalModalType} onClose={() => setLegalModalType(null)} />
      )}
    </div>
  );
}

export default LandingPage;

const bootstrapStyles = {
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #c2f3ef 0%, #57c0b2 30%, #94e4c9 100%)',
  },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid rgba(255, 255, 255, 0.4)',
    borderTopColor: '#FFFFFF',
    borderRadius: '50%',
    animation: 'landing-bootstrap-spin 0.9s linear infinite',
  },
};

if (typeof document !== 'undefined' && !document.getElementById('landing-bootstrap-keyframes')) {
  const style = document.createElement('style');
  style.id = 'landing-bootstrap-keyframes';
  style.textContent = `@keyframes landing-bootstrap-spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(style);
}